import React, { Suspense, useEffect, useState } from 'react';
import { useRoutes } from 'react-router-dom';
// layouts
import DefaultLayout from 'src/layouts/Default';
import VerticalLayout from 'src/layouts/Vertical';
import HorizontalLayout from 'src/layouts/Horizontal/';
import Loader from 'src/components/Loader';

// components
import PrivateRoute from './PrivateRoute';
import Root from './Root';

// constants
import { LayoutTypes } from 'src/constants';

// hooks
import { useRedux } from 'src/hooks';

// lazy load all the views
// auth
const Index = React.lazy(() => import('src/pages/auth'));
const Login = React.lazy(() => import('src/pages/auth/Login'));
const NewLogin = React.lazy(() => import('src/pages/auth/NewLogin'));
const Register = React.lazy(() => import('src/pages/auth/Register'));
const LabRegister = React.lazy(() => import('src/pages/auth/LabRegister'));
const Confirm = React.lazy(() => import('src/pages/auth/Confirm'));
const ForgetPassword = React.lazy(() => import('src/pages/auth/ForgetPassword'));
const LockScreen = React.lazy(() => import('src/pages/auth/LockScreen'));
const ResetPassword = React.lazy(() => import('src/pages/ResetPassword'));
const Logout = React.lazy(() => import('src/pages/auth/Logout'));
const Profile = React.lazy(() => import('src/pages/apps/Contacts/Profile'));

const Panel = React.lazy(() => import('src/pages/Panel/PanelListView'));
const PanelCreate = React.lazy(() => import('src/pages/Panel/PanelCreateView'));
const PanelEdit = React.lazy(() => import('src/pages/Panel/PanelEditView'));
const PanelView = React.lazy(() => import('src/pages/Panel/PanelDetailsView'));

const PanelCategory = React.lazy(() => import('src/pages/PanelCategory/PanelCategoryListView'));
const PanelCategoryCreate = React.lazy(() => import('src/pages/PanelCategory/PanelCategoryCreateView'));
const PanelCategoryEdit = React.lazy(() => import('src/pages/PanelCategory/PanelCategoryEditView'));

const Test = React.lazy(() => import('src/pages/Test/TestListView'));
const TestCreate = React.lazy(() => import('src/pages/Test/TestCreateView'));
const TestEdit = React.lazy(() => import('src/pages/Test/TestEditView'));
const TestView = React.lazy(() => import('src/pages/Test/TestDetailsView'));

const TestCategory = React.lazy(() => import('src/pages/TestCategory/TestCategoryListView'));
const TestCategoryCreate = React.lazy(() => import('src/pages/TestCategory/TestCategoryCreateView'));
const TestCategoryEdit = React.lazy(() => import('src/pages/TestCategory/TestCategoryEditView'));

const Employee = React.lazy(() => import('src/pages/Employee/EmployeeListView'));
const EmployeeCreate = React.lazy(() => import('src/pages/Employee/EmployeeCreateView'));
const EmployeeEdit = React.lazy(() => import('src/pages/Employee/EmployeeEditView'));

const LetterHead = React.lazy(() => import('src/pages/LetterHead/LetterHeadListView'));
const LetterHeadCreate = React.lazy(() => import('src/pages/LetterHead/LetterHeadCreateView'));
const LetterHeadCreateEdit = React.lazy(() => import('src/pages/LetterHead/LetterHeadCreateEditView'));
const LetterHeadDesign = React.lazy(() => import('src/pages/LetterHead/LetterHeadDesign'));

const LabMemberShip = React.lazy(() => import('src/pages/LabMemberShip/LabMemberShipListView'));
const LabMemberShipCreate = React.lazy(() => import('src/pages/LabMemberShip/LabMemberShipCreateView'));
const LabMemberShipEdit = React.lazy(() => import('src/pages/LabMemberShip/LabMemberShipEditView'));

const LabRegistration = React.lazy(() => import('src/pages/LabRegistration/LabRegistrationListView'));
const LabRegistrationCreate = React.lazy(() => import('src/pages/LabRegistration/LabRegistrationCreateView'));
const LabRegistrationEdit = React.lazy(() => import('src/pages/LabRegistration/LabRegistrationEditView'));

const LabSubCenter = React.lazy(() => import('src/pages/LabSubCenter/LabSubCenterListView'));
const LabSubCenterCreate = React.lazy(() => import('src/pages/LabSubCenter/LabSubCenterCreateView'));
const LabSubCenterEdit = React.lazy(() => import('src/pages/LabSubCenter/LabSubCenterEditView'));

const ContactPerson = React.lazy(() => import('src/pages/ContactPerson/ContactPersonListView'));
const ContactPersonCreate = React.lazy(() => import('src/pages/ContactPerson/ContactPersonCreateView'));
const ContactPersonEdit = React.lazy(() => import('src/pages/ContactPerson/ContactPersonEditView'));

const TestRateCard = React.lazy(() => import('src/pages/TestRateCard/TestRateCardListView'));
const TestRateCardCreate = React.lazy(() => import('src/pages/TestRateCard/TestRateCardCreateView'));
const TestRateCardEdit = React.lazy(() => import('src/pages/TestRateCard/TestRateCardEditView'));

const PanelRateCard = React.lazy(() => import('src/pages/PanelRateCard/PanelRateCardListView'));
const PanelRateCardCreate = React.lazy(() => import('src/pages/PanelRateCard/PanelRateCardCreateView'));
const PanelRateCardEdit = React.lazy(() => import('src/pages/PanelRateCard/PanelRateCardEditView'));

const DiscountCoupon = React.lazy(() => import('src/pages/DiscountCoupon/DiscountCouponListView'));
const DiscountCouponCreate = React.lazy(() => import('src/pages/DiscountCoupon/DiscountCouponCreateView'));
const DiscountCouponEdit = React.lazy(() => import('src/pages/DiscountCoupon/DiscountCouponEditView'));

const Patient = React.lazy(() => import('src/pages/Patient/PatientListView'));
const PatientCreate = React.lazy(() => import('src/pages/Patient/PatientCreateView'));
const PatientEdit = React.lazy(() => import('src/pages/Patient/PatientEditView'));
const PatientView = React.lazy(() => import('src/pages/Patient/PatientDetailsView'));

const EncounterView = React.lazy(() => import('src/pages/Encounter/EncounterListView'));
const EncounterCreate = React.lazy(() => import('src/pages/Encounter/EncounterCreateView'));

const EncounterWizard = React.lazy(() => import('src/pages/Encounter/EncounterWizardView'));
const EncounterEditWizard = React.lazy(() => import('src/pages/Encounter/EncounterEditWizardView'));
const EncounterViewWizard = React.lazy(() => import('src/pages/Encounter/EncounterViewWizardView'));
const PatientEncounterView = React.lazy(() => import('src/pages/Encounter/PatientEncounterListView'));

const EncounterObservationCreateView = React.lazy(() => import('src/pages/Observation/EncounterObservationCreateView'));
const EncounterObservationReportView = React.lazy(() => import('src/pages/Observation/EncounterObservationReportView'));
const EncounterOPDServiceCreateView = React.lazy(() => import('src/pages/OPDServices/OPDServicesCreateView'));

const EncounterObservationReportPDFView = React.lazy(
    () => import('src/pages/Observation/EncounterObservationReportView/report')
);
const EncounterObservationReportListView = React.lazy(() => import('src/pages/Observation/ReportListView'));

const EncounterObservationSummaryView = React.lazy(
    () => import('src/pages/Observation/EncounterObservationSummaryView')
);

const UserView = React.lazy(() => import('src/pages/User/UserListView'));
const UserCreateView = React.lazy(() => import('src/pages/User/UserCreateView'));
const UserEditView = React.lazy(() => import('src/pages/User/UserEditView'));

const CredExpView = React.lazy(() => import('src/pages/CredExpInfo/CredExpInfoListView'));
const CredExpCreateView = React.lazy(() => import('src/pages/CredExpInfo/CredExpInfoCreateView'));
const CredExpEditView = React.lazy(() => import('src/pages/CredExpInfo/CredExpInfoEditView'));

const NotifyView = React.lazy(() => import('src/pages/Notify/NotifyListView'));
const NotifyCreateView = React.lazy(() => import('src/pages/Notify/NotifyCreateView'));
const NotifyEditView = React.lazy(() => import('src/pages/Notify/NotifyEditView'));

const ReportView = React.lazy(() => import('src/pages/Report/ReportListView'));
const ServiceReportView = React.lazy(() => import('src/pages/ServicesReport/ReportListView'));
const SalesReportView = React.lazy(() => import('src/pages/SalesReport/ReportListView'));
const ObservationReportView = React.lazy(() => import('src/pages/ObservationReport/ReportListView'));
const ObservationReportListView = React.lazy(() => import('src/pages/ObservationReport/ObservationReportView'));

const RevenueReportView = React.lazy(() => import('src/pages/RevenueReport/ReportListView'));
// dashboards
const DashBoard1 = React.lazy(() => import('src/pages/dashboards/DashBoard1/'));
const INDashBoard1 = React.lazy(() => import('src/pages/indashboards/DashBoard1/'));
const DoctorDashBoard1 = React.lazy(() => import('src/pages/doctorDashboards/DashBoard1/'));

const TestParamMappingView = React.lazy(() => import('src/pages/TestParamMapping/TestParamMappingListView'));
const TestParamMappingCreateView = React.lazy(() => import('src/pages/TestParamMapping/TestParamMappingCreateView'));
const TestParamMappingEditView = React.lazy(() => import('src/pages/TestParamMapping/TestParamMappingEditView'));

const TestPanelMappingView = React.lazy(() => import('src/pages/TestPanelMapping/TestPanelMappingListView'));
const TestPanelMappingCreateView = React.lazy(() => import('src/pages/TestPanelMapping/TestPanelMappingCreateView'));
const TestPanelMappingEditView = React.lazy(() => import('src/pages/TestPanelMapping/TestPanelMappingEditView'));

const RangeDefView = React.lazy(() => import('src/pages/RangeDef/RangeDefListView'));
const RangeDefCreateView = React.lazy(() => import('src/pages/RangeDef/RangeDefCreateView'));
const RangeDefEditView = React.lazy(() => import('src/pages/RangeDef/RangeDefEditView'));

const ResultSetView = React.lazy(() => import('src/pages/ResultSet/ResultSetListView'));
const ResultSetCreateView = React.lazy(() => import('src/pages/ResultSet/ResultSetCreateView'));
const ResultSetEditView = React.lazy(() => import('src/pages/ResultSet/ResultSetEditView'));

const UOMView = React.lazy(() => import('src/pages/UOM/UOMListView'));
const UOMCreateView = React.lazy(() => import('src/pages/UOM/UOMCreateView'));
const UOMEditView = React.lazy(() => import('src/pages/UOM/UOMEditView'));

const ImportView = React.lazy(() => import('src/pages/Import/ImportView'));

const AssociatedListView = React.lazy(() => import('src/pages/AssociatedList/AssociatedListView'));
const AssociatedListCreate = React.lazy(() => import('src/pages/AssociatedList/AssociatedListCreateView'));
const AssociatedListEdit = React.lazy(() => import('src/pages/AssociatedList/AssociatedListEditView'));

const ManageUser = React.lazy(() => import('src/pages/ManageUser'));

const loading = () => (
    <div className="">
        <Loader />
    </div>
);

type LoadComponentProps = {
    component: React.LazyExoticComponent<() => JSX.Element>;
};

const LoadComponent = ({ component: Component }: LoadComponentProps) => (
    <Suspense fallback={loading()}>
        <Component />
    </Suspense>
);

const AllRoutes = () => {
    const { appSelector } = useRedux();
    const loginUser: any = sessionStorage.getItem('admin_user');
    const [updateData, setUpdatedData] = useState<any>({});
    useEffect(() => {
        if (loginUser) {
            const newData = JSON.parse(loginUser);

            setUpdatedData(newData);
        }
    }, [loginUser]);
    const { layout } = appSelector((state) => ({
        layout: state.Layout,
    }));

    const getLayout = () => {
        let layoutCls: React.ComponentType = VerticalLayout;

        switch (layout.layoutType) {
            case LayoutTypes.LAYOUT_HORIZONTAL:
                layoutCls = HorizontalLayout;
                break;
            default:
                layoutCls = VerticalLayout;
                break;
        }
        return layoutCls;
    };
    let Layout = getLayout();

    return useRoutes([
        { path: '/', element: <Root /> },
        {
            // public routes
            path: '/',
            element: <DefaultLayout />,
            children: [
                {
                    path: 'auth',
                    children: [
                        { path: 'login', element: <LoadComponent component={Index} /> },
                        { path: 'lab-login', element: <LoadComponent component={Login} /> },
                        { path: 'individual-login', element: <LoadComponent component={NewLogin} /> },
                        { path: 'lab-register', element: <LoadComponent component={LabRegister} /> },
                        { path: 'register', element: <LoadComponent component={Register} /> },
                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                        {
                            path: 'encounter/:patientNo/:encounterId/observation-report/:labId/:subcenId/:subcenTy',
                            element: <LoadComponent component={EncounterObservationReportPDFView} />,
                        },
                    ],
                },
            ],
        },
        {
            // auth protected routes
            path: '/',
            element: <PrivateRoute roles={'Admin'} component={Layout} />,
            children: [
                {
                    path: 'dashboard',
                    element: (
                        <LoadComponent
                            component={updateData?.UserProfile === 'Doctor' ? DoctorDashBoard1 : DashBoard1}
                        />
                    ),
                },
                {
                    path: 'individual/dashboard',
                    element: <LoadComponent component={INDashBoard1} />,
                },
                {
                    path: 'apps',
                    children: [
                        {
                            path: 'contacts/profile',
                            element: <LoadComponent component={Profile} />,
                        },
                    ],
                },
                {
                    path: 'management/panels',
                    element: <LoadComponent component={Panel} />,
                },
                {
                    path: 'management/panel/create',
                    element: <LoadComponent component={PanelCreate} />,
                },
                {
                    path: 'management/panel/:id/edit',
                    element: <LoadComponent component={PanelEdit} />,
                },
                {
                    path: 'management/panel/:id/view',
                    element: <LoadComponent component={PanelView} />,
                },
                {
                    path: 'management/panel-category',
                    element: <LoadComponent component={PanelCategory} />,
                },
                {
                    path: 'management/panel-category/create',
                    element: <LoadComponent component={PanelCategoryCreate} />,
                },
                {
                    path: 'management/panel-category/:id/edit',
                    element: <LoadComponent component={PanelCategoryEdit} />,
                },
                {
                    path: 'management/tests',
                    element: <LoadComponent component={Test} />,
                },
                {
                    path: 'management/test/create',
                    element: <LoadComponent component={TestCreate} />,
                },
                {
                    path: 'management/test-category',
                    element: <LoadComponent component={TestCategory} />,
                },
                {
                    path: 'management/test-category/create',
                    element: <LoadComponent component={TestCategoryCreate} />,
                },
                {
                    path: 'management/test-category/:id/edit',
                    element: <LoadComponent component={TestCategoryEdit} />,
                },
                {
                    path: 'management/test/:id/edit',
                    element: <LoadComponent component={TestEdit} />,
                },
                {
                    path: 'management/test/:id/view',
                    element: <LoadComponent component={TestView} />,
                },
                {
                    path: 'management/employee/',
                    element: <LoadComponent component={Employee} />,
                },
                {
                    path: 'management/employee/create',
                    element: <LoadComponent component={EmployeeCreate} />,
                },
                {
                    path: 'management/employee/:id/edit',
                    element: <LoadComponent component={EmployeeEdit} />,
                },
                {
                    path: 'management/letter-head',
                    element: <LoadComponent component={LetterHead} />,
                },
                {
                    path: 'management/letter-head/create',
                    element: <LoadComponent component={LetterHeadCreate} />,
                },
                {
                    path: 'management/letter-head/:id/edit',
                    element: <LoadComponent component={LetterHeadCreateEdit} />,
                },
                {
                    path: 'management/letter-head/:id/view',
                    element: <LoadComponent component={LetterHeadDesign} />,
                },
                {
                    path: 'management/lab-membership',
                    element: <LoadComponent component={LabMemberShip} />,
                },
                {
                    path: 'management/lab-membership/create',
                    element: <LoadComponent component={LabMemberShipCreate} />,
                },
                {
                    path: 'management/lab-membership/:id/edit',
                    element: <LoadComponent component={LabMemberShipEdit} />,
                },
                {
                    path: 'management/lab-registration',
                    element: <LoadComponent component={LabRegistration} />,
                },
                {
                    path: 'management/lab-registration/create',
                    element: <LoadComponent component={LabRegistrationCreate} />,
                },
                {
                    path: 'management/lab-registration/:id/edit',
                    element: <LoadComponent component={LabRegistrationEdit} />,
                },
                {
                    path: 'management/lab-sub-center',
                    element: <LoadComponent component={LabSubCenter} />,
                },
                {
                    path: 'management/lab-sub-center/create',
                    element: <LoadComponent component={LabSubCenterCreate} />,
                },
                {
                    path: 'management/lab-sub-center/:id/edit',
                    element: <LoadComponent component={LabSubCenterEdit} />,
                },
                {
                    path: 'management/contact-person',
                    element: <LoadComponent component={ContactPerson} />,
                },
                {
                    path: 'management/contact-person/create',
                    element: <LoadComponent component={ContactPersonCreate} />,
                },
                {
                    path: 'management/contact-person/:id/edit',
                    element: <LoadComponent component={ContactPersonEdit} />,
                },
                {
                    path: 'management/test-rate-card',
                    element: <LoadComponent component={TestRateCard} />,
                },
                {
                    path: 'management/test-rate-card/create',
                    element: <LoadComponent component={TestRateCardCreate} />,
                },
                {
                    path: 'management/test-rate-card/:id/edit',
                    element: <LoadComponent component={TestRateCardEdit} />,
                },
                {
                    path: 'management/panel-rate-card',
                    element: <LoadComponent component={PanelRateCard} />,
                },
                {
                    path: 'management/panel-rate-card/create',
                    element: <LoadComponent component={PanelRateCardCreate} />,
                },
                {
                    path: 'management/panel-rate-card/:id/edit',
                    element: <LoadComponent component={PanelRateCardEdit} />,
                },
                {
                    path: 'management/discount-coupon',
                    element: <LoadComponent component={DiscountCoupon} />,
                },
                {
                    path: 'management/discount-coupon/create',
                    element: <LoadComponent component={DiscountCouponCreate} />,
                },
                {
                    path: 'management/discount-coupon/:id/edit',
                    element: <LoadComponent component={DiscountCouponEdit} />,
                },
                {
                    path: 'management/patient',
                    element: <LoadComponent component={Patient} />,
                },
                {
                    path: 'management/patient/create',
                    element: <LoadComponent component={PatientCreate} />,
                },
                {
                    path: 'management/patient/:id/edit',
                    element: <LoadComponent component={PatientEdit} />,
                },
                {
                    path: 'management/patient/:id/view',
                    element: <LoadComponent component={PatientView} />,
                },
                {
                    path: 'management/encounter',
                    element: <LoadComponent component={EncounterView} />,
                },
                {
                    path: 'management/encounter/:patientNo/getEncounters',
                    element: <LoadComponent component={PatientEncounterView} />,
                },
                {
                    path: 'management/:patientNo/encounter',
                    element: <LoadComponent component={EncounterWizard} />,
                },
                {
                    path: 'management/encounter/:patientNo/create',
                    element: <LoadComponent component={EncounterWizard} />,
                },
                {
                    path: 'management/:patientNo/:encounterId/encounter/create',
                    element: <LoadComponent component={EncounterWizard} />,
                },
                {
                    path: 'management/encounter/:patientNo/:encounterId/edit',
                    element: <LoadComponent component={EncounterEditWizard} />,
                },
                {
                    path: 'management/encounter/:patientNo/:encounterId/view',
                    element: <LoadComponent component={EncounterViewWizard} />,
                },
                {
                    path: 'management/encounter/:patientNo/:encounterId/observation',
                    element: <LoadComponent component={EncounterObservationCreateView} />,
                },
                {
                    path: 'management/encounter/:patientNo/:encounterId/observation-report',
                    element: <LoadComponent component={EncounterObservationReportView} />,
                },
                {
                    path: 'management/encounter/:patientNo/:encounterId/observation/report',
                    element: <LoadComponent component={EncounterObservationReportListView} />,
                },
                {
                    path: 'management/encounter/:patientNo/:encounterId/opd-services',
                    element: <LoadComponent component={EncounterOPDServiceCreateView} />,
                },
                // },
                // {
                //     path: 'management/encounter/:patientNo/:encounterId/service-reports',
                //     element: <LoadComponent component={ServiceReportView} />,
                // },
                {
                    path: 'management/service-reports',
                    element: <LoadComponent component={ServiceReportView} />,
                },
                {
                    path: 'management/sale-report',
                    element: <LoadComponent component={SalesReportView} />,
                },
                {
                    path: 'management/observation-reports',
                    element: <LoadComponent component={ObservationReportView} />,
                },
                {
                    path: 'management/revenue-reports',
                    element: <LoadComponent component={RevenueReportView} />,
                },
                {
                    path: 'management/observation-reports/:patientNo/:encounterId/ObservationReportListView',
                    element: <LoadComponent component={ObservationReportListView} />,
                },
                {
                    path: 'management/user',
                    element: <LoadComponent component={UserView} />,
                },
                {
                    path: 'management/user/create',
                    element: <LoadComponent component={UserCreateView} />,
                },
                {
                    path: 'management/user/:id/edit',
                    element: <LoadComponent component={UserEditView} />,
                },
                {
                    path: 'management/cred-exp-info',
                    element: <LoadComponent component={CredExpView} />,
                },
                {
                    path: 'management/cred-exp-info/create',
                    element: <LoadComponent component={CredExpCreateView} />,
                },
                {
                    path: 'management/cred-exp-info/:id/edit',
                    element: <LoadComponent component={CredExpEditView} />,
                },
                {
                    path: 'management/notify-settings',
                    element: <LoadComponent component={NotifyView} />,
                },
                {
                    path: 'management/notify-settings/create',
                    element: <LoadComponent component={NotifyCreateView} />,
                },
                {
                    path: 'management/notify-settings/:id/edit',
                    element: <LoadComponent component={NotifyEditView} />,
                },
                {
                    path: 'management/lab-reports',
                    element: <LoadComponent component={ReportView} />,
                },
                {
                    path: 'management/test-param-mapping',
                    element: <LoadComponent component={TestParamMappingView} />,
                },
                {
                    path: 'management/test-param-mapping/create',
                    element: <LoadComponent component={TestParamMappingCreateView} />,
                },
                {
                    path: 'management/test-param-mapping/:id/edit',
                    element: <LoadComponent component={TestParamMappingEditView} />,
                },
                {
                    path: 'management/test-panel-mapping',
                    element: <LoadComponent component={TestPanelMappingView} />,
                },
                {
                    path: 'management/test-panel-mapping/create',
                    element: <LoadComponent component={TestPanelMappingCreateView} />,
                },
                {
                    path: 'management/test-panel-mapping/:id/edit',
                    element: <LoadComponent component={TestPanelMappingEditView} />,
                },
                {
                    path: 'management/range-definition',
                    element: <LoadComponent component={RangeDefView} />,
                },
                {
                    path: 'management/range-definition/create',
                    element: <LoadComponent component={RangeDefCreateView} />,
                },
                {
                    path: 'management/range-definition/:id/edit',
                    element: <LoadComponent component={RangeDefEditView} />,
                },
                {
                    path: 'management/result-set',
                    element: <LoadComponent component={ResultSetView} />,
                },
                {
                    path: 'management/result-set/create',
                    element: <LoadComponent component={ResultSetCreateView} />,
                },
                {
                    path: 'management/result-set/:id/edit',
                    element: <LoadComponent component={ResultSetEditView} />,
                },
                {
                    path: 'management/uom',
                    element: <LoadComponent component={UOMView} />,
                },
                {
                    path: 'management/uom/create',
                    element: <LoadComponent component={UOMCreateView} />,
                },
                {
                    path: 'management/uom/:id/edit',
                    element: <LoadComponent component={UOMEditView} />,
                },
                {
                    path: 'management/reset-password',
                    element: <LoadComponent component={ResetPassword} />,
                },
                {
                    path: 'management/import',
                    element: <LoadComponent component={ImportView} />,
                },

                {
                    path: 'management/associated-list',
                    element: <LoadComponent component={AssociatedListView} />,
                },
                {
                    path: 'management/associated-list/create',
                    element: <LoadComponent component={AssociatedListCreate} />,
                },
                {
                    path: 'management/associated-list/:id/edit',
                    element: <LoadComponent component={AssociatedListEdit} />,
                },
                {
                    path: 'management/manage-user',
                    element: <LoadComponent component={ManageUser} />,
                },
            ],
        },
    ]);
};

export { AllRoutes };
